import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  boolean,
  jsonb,
  index,
  uniqueIndex,
  check,
  type AnyPgColumn,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// JSONB type interfaces
// ---------------------------------------------------------------------------

export interface PageSettings {
  favicon?: string;
  customCss?: string;
  headScripts?: string;
  bodyEndScripts?: string;
  googleFonts?: string[];
  mood?: "minimal" | "bold" | "elegant" | "playful" | "dark";
  showScrollProgress?: boolean;
  showBackToTop?: boolean;
  [key: string]: unknown;
}

export interface SeoMeta {
  title?: string;
  description?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  canonicalUrl?: string;
  robots?: string;
  jsonLd?: Record<string, unknown>[];
  [key: string]: unknown;
}

export interface ReviewAnchorPosition {
  x: number;
  y: number;
}

// ---------------------------------------------------------------------------
// Status enums
// ---------------------------------------------------------------------------

export const PAGE_STATUSES = ["draft", "published", "archived"] as const;

export const PAGE_TYPES = [
  "lead_capture",
  "webinar",
  "sales",
  "booking",
  "thank_you",
  "link_in_bio",
  "custom",
  "site_page",
  "blog_post",
  "funnel_step",
  "course_lesson",
  "course_exercise",
  "certificate",
] as const;

export const GENERATION_SOURCES = ["ai", "manual", "duplicate", "autosave"] as const;
export const PAGE_REVIEW_STATUSES = ["pending", "changes_requested", "approved"] as const;
export const PAGE_REVIEW_AUTHOR_ROLES = ["internal", "client"] as const;

export const REDIRECT_TYPES = [301, 302] as const;

export type PageStatus = (typeof PAGE_STATUSES)[number];
export type PageType = (typeof PAGE_TYPES)[number];
export type GenerationSource = (typeof GENERATION_SOURCES)[number];
export type PageReviewStatus = (typeof PAGE_REVIEW_STATUSES)[number];
export type PageReviewAuthorRole = (typeof PAGE_REVIEW_AUTHOR_ROLES)[number];

// ---------------------------------------------------------------------------
// 1. Pages
// ---------------------------------------------------------------------------

export const pages = pgTable(
  "pages",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    slug: text("slug").notNull(),
    title: text("title").notNull(),
    description: text("description"),
    status: text("status").notNull().default("draft"),
    pageType: text("page_type").notNull().default("custom"),
    currentVersionId: uuid("current_version_id"),
    publishedVersionId: uuid("published_version_id"),
    folderId: uuid("folder_id"),
    settings: jsonb("settings").$type<PageSettings>().notNull().default({}),
    seoMeta: jsonb("seo_meta").$type<SeoMeta>().notNull().default({}),
    createdBy: text("created_by").notNull(),
    updatedBy: text("updated_by"),
    publishedAt: timestamp("published_at", tz),
    publishedUrl: text("published_url"),
    scheduledPublishAt: timestamp("scheduled_publish_at", tz),
    scheduledPublishVersionId: uuid("scheduled_publish_version_id"),
    customDomainId: uuid("custom_domain_id"),
    // FK to sites.id — cannot use .references() here due to circular import
    // (page-builder-sites.ts imports pages). Applied via raw SQL migration:
    // ALTER TABLE pages ADD CONSTRAINT fk_pages_site_id
    //   FOREIGN KEY (site_id) REFERENCES sites(id) ON DELETE CASCADE;
    siteId: uuid("site_id"),
    sitePath: text("site_path"),
    isHomePage: boolean("is_home_page").notNull().default(false),
    version: integer("version").notNull().default(1),
    deletedAt: timestamp("deleted_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_pages_tenant_slug").on(table.tenantId, table.slug),
    index("idx_pages_tenant_status").on(table.tenantId, table.status),
    index("idx_pages_scheduled_publish").on(table.tenantId, table.scheduledPublishAt),
    index("idx_pages_tenant_folder").on(table.tenantId, table.folderId),
    index("idx_pages_tenant_created").on(table.tenantId, table.createdAt),
    index("idx_pages_tenant").on(table.tenantId),
    check(
      "chk_pages_status",
      sql`${table.status} IN ('draft', 'published', 'archived')`,
    ),
    check(
      "chk_pages_page_type",
      sql`${table.pageType} IN ('lead_capture', 'webinar', 'sales', 'booking', 'thank_you', 'link_in_bio', 'custom', 'site_page', 'blog_post', 'funnel_step', 'course_lesson', 'course_exercise', 'certificate')`,
    ),
    index("idx_pages_site_id").on(table.siteId),
    // Partial unique index — enforced via raw SQL migration:
    // CREATE UNIQUE INDEX idx_pages_site_path ON pages(site_id, site_path)
    //   WHERE site_id IS NOT NULL AND site_path IS NOT NULL;
    // Drizzle does not support partial indexes, so this is a non-unique
    // index for query performance. The partial unique constraint prevents
    // duplicate paths within a site while allowing standalone pages
    // (NULL siteId) to coexist without conflicts.
    index("idx_pages_site_path").on(table.siteId, table.sitePath),
  ],
);

// ---------------------------------------------------------------------------
// 2. Page Versions
// ---------------------------------------------------------------------------

export const pageVersions = pgTable(
  "page_versions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    pageId: uuid("page_id")
      .notNull()
      .references(() => pages.id, { onDelete: "cascade" }),
    tenantId: uuid("tenant_id").notNull(),
    versionNumber: integer("version_number").notNull(),
    puckData: jsonb("puck_data").$type<Record<string, unknown>>().notNull(),
    bindings: jsonb("bindings")
      .$type<Record<string, unknown>>()
      .notNull()
      .default({}),
    thumbnail: text("thumbnail"),
    generationSource: text("generation_source").notNull().default("manual"),
    aiPrompt: text("ai_prompt"),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_page_versions_page_number").on(
      table.pageId,
      table.versionNumber,
    ),
    index("idx_page_versions_tenant").on(table.tenantId),
    index("idx_page_versions_page").on(table.pageId),
    check(
      "chk_page_versions_source",
      sql`${table.generationSource} IN ('ai', 'manual', 'duplicate', 'autosave')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// 2b. Page Reviews
// ---------------------------------------------------------------------------

export const pageReviews = pgTable(
  "page_reviews",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    pageId: uuid("page_id")
      .notNull()
      .references(() => pages.id, { onDelete: "cascade" }),
    versionId: uuid("version_id")
      .notNull()
      .references(() => pageVersions.id, { onDelete: "cascade" }),
    siteId: uuid("site_id"),
    shareToken: text("share_token").notNull(),
    status: text("status").notNull().default("pending"),
    reviewerName: text("reviewer_name"),
    reviewerEmail: text("reviewer_email"),
    message: text("message"),
    requestedBy: text("requested_by").notNull(),
    requestedAt: timestamp("requested_at", tz).notNull().defaultNow(),
    lastDecisionBy: text("last_decision_by"),
    lastDecisionAt: timestamp("last_decision_at", tz),
    approvedAt: timestamp("approved_at", tz),
    changesRequestedAt: timestamp("changes_requested_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_page_reviews_share_token").on(table.shareToken),
    index("idx_page_reviews_tenant_page").on(table.tenantId, table.pageId),
    index("idx_page_reviews_tenant_version").on(table.tenantId, table.versionId),
    check(
      "chk_page_reviews_status",
      sql`${table.status} IN ('pending', 'changes_requested', 'approved')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// 3. Page Folders
// ---------------------------------------------------------------------------

export const pageFolders = pgTable(
  "page_folders",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    parentId: uuid("parent_id").references((): AnyPgColumn => pageFolders.id, {
      onDelete: "set null",
    }),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_page_folders_tenant_parent").on(table.tenantId, table.parentId),
    index("idx_page_folders_tenant").on(table.tenantId),
    uniqueIndex("idx_page_folders_tenant_parent_name").on(
      table.tenantId,
      table.parentId,
      table.name,
    ),
  ],
);

// ---------------------------------------------------------------------------
// 4. Page Redirects
// ---------------------------------------------------------------------------

export const pageRedirects = pgTable(
  "page_redirects",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    fromSlug: text("from_slug").notNull(),
    toSlug: text("to_slug").notNull(),
    redirectType: integer("redirect_type").notNull().default(301),
    domainId: uuid("domain_id"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_page_redirects_tenant_slug_domain").on(
      table.tenantId,
      table.fromSlug,
      table.domainId,
    ),
    index("idx_page_redirects_tenant").on(table.tenantId),
    check(
      "chk_page_redirects_type",
      sql`${table.redirectType} IN (301, 302)`,
    ),
  ],
);
